import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, X, Plus, ChevronDown, RotateCcw } from "lucide-react";
import { AppFrame } from "@/components/AppFrame";
import { DoseDetail } from "@/components/DoseDetail";
import { api, cachedGet, getFresh, todayStr, shiftDate, fmt12, nowHM, effectiveStatus, lateLabel, overdueLabel, fmtActed, fmtDayLabel } from "@/lib/api";
import { useReminders } from "@/hooks/useReminders";
import { useLargeFont } from "@/hooks/useLargeFont";
import { useProfile } from "@/context/ProfileContext";

const Action = ({ testid, onClick, tone, big, children }) => {
  const tones = {
    green: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
    red: "bg-red-500/12 text-red-300/90 ring-red-500/25",
    slate: "bg-slate-800/70 text-slate-300 ring-slate-700",
  };
  return (
    <button
      data-testid={testid}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`flex shrink-0 items-center justify-center rounded-full ring-1 transition-transform duration-150 active:scale-90 ${
        big ? "h-12 w-12" : "h-8 w-8"
      } ${tones[tone]}`}
    >
      {children}
    </button>
  );
};

/** Compact inline status: glowing "3.5h" when late, "Missed" for past days, original time when snoozed. */
const Meta = ({ item, isToday, testKey, big }) => {
  const snoozed = item.eff === "snoozed" || item.eff === "snooze-due";
  const late = ["overdue", "snooze-due", "missed"].includes(item.eff);

  if (big) {
    if (!snoozed && !late) return null;
    return (
      <p className="mt-1 whitespace-nowrap text-[16px] leading-snug">
        {late && isToday && (
          <span
            className="text-sky-300 [text-shadow:0_0_12px_rgba(56,189,248,0.45)]"
            data-testid={`overdue-label-${testKey}`}
          >
            {overdueLabel(item.time)}
          </span>
        )}
        {late && !isToday && <span className="text-slate-500">Missed</span>}
        {snoozed && late && <span className="text-slate-600"> · </span>}
        {snoozed && (
          <span className="text-slate-400" data-testid={`snoozed-time-${testKey}`}>
            Original: {fmt12(item.time)}
          </span>
        )}
      </p>
    );
  }

  return (
    <>
      {late && isToday && (
        <span
          data-testid={`overdue-label-${testKey}`}
          className="shrink-0 rounded-full bg-sky-500/10 px-1.5 py-[1px] text-[11px] font-semibold text-sky-300 ring-1 ring-inset ring-sky-500/25 [text-shadow:0_0_10px_rgba(56,189,248,0.55)]"
        >
          {lateLabel(item.time)}
        </span>
      )}
      {late && !isToday && <span className="shrink-0 text-[11px] text-slate-600">Missed</span>}
      {snoozed && (
        <span className="shrink-0 text-[11px] text-slate-500" data-testid={`snoozed-time-${testKey}`}>
          was {fmt12(item.time)}
        </span>
      )}
    </>
  );
};

export default function DayTimeline() {
  const { active } = useProfile();
  const big = useLargeFont();
  const [searchParams] = useSearchParams();
  const readOnly = active && !active.is_self && active.access === "view";
  const [date, setDate] = useState(() => searchParams.get("date") || todayStr());
  const profileKey = active?.user_id || "self";
  const cacheKey = useCallback((d) => ({ date: d, p: profileKey }), [profileKey]);
  const [loaded, setLoaded] = useState({ date: null, items: null });
  const [openItem, setOpenItem] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const [, setTick] = useState(0);
  const dir = useRef(0);

  // derived during render, so a new date can never paint with the previous day's rows
  const items = loaded.date === date ? loaded.items : cachedGet("/schedule", cacheKey(date)) ?? null;
  const setItems = (next) =>
    setLoaded((prev) => ({
      date,
      items: typeof next === "function" ? next(prev.date === date ? prev.items : items) : next,
    }));

  const refresh = useCallback(async () => {
    try {
      setLoaded({ date, items: await getFresh("/schedule", { date }, cacheKey(date)) });
    } catch {
      toast.error("Could not load this day");
      setLoaded({ date, items: [] });
    }
    // warm the neighbours so the next swipe is instant (the memo cache is wiped after every action)
    [shiftDate(date, 1), shiftDate(date, -1)].forEach((d) => {
      if (!cachedGet("/schedule", cacheKey(d))) getFresh("/schedule", { date: d }, cacheKey(d)).catch(() => {});
    });
  }, [date, cacheKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  useReminders(items, date);

  const isToday = date === todayStr();

  const { groups, done, counters } = useMemo(() => {
    const perMed = {};
    (items || [])
      .slice()
      .sort((a, b) => a.time.localeCompare(b.time))
      .forEach((i) => {
        perMed[i.med_id] = perMed[i.med_id] || [];
        perMed[i.med_id].push(i.time);
      });
    const counterMap = {};
    Object.entries(perMed).forEach(([medId, times]) => {
      if (times.length > 1) times.forEach((t, idx) => (counterMap[`${medId}-${t}`] = `${idx + 1}/${times.length}`));
    });

    const pending = [];
    const doneItems = [];
    (items || []).forEach((i) => {
      const eff = effectiveStatus(i, date);
      if (eff === "taken" || eff === "skipped") doneItems.push({ ...i, eff });
      else pending.push({ ...i, eff, due: i.snooze_until || i.time });
    });
    const map = new Map();
    pending.forEach((i) => {
      if (!map.has(i.due)) map.set(i.due, []);
      map.get(i.due).push(i);
    });
    return {
      groups: [...map.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([time, list]) => ({ time, list })),
      done: doneItems.sort((a, b) => a.time.localeCompare(b.time)),
      counters: counterMap,
    };
  }, [items, date]);

  const act = async (item, status, extra = {}) => {
    if (readOnly) return toast.error("You have view-only access");
    setItems((prev) =>
      (prev || []).map((p) =>
        p.med_id === item.med_id && p.time === item.time
          ? { ...p, status, snooze_until: status === "snoozed" ? extra.snooze_until || p.snooze_until : null }
          : p,
      ),
    );
    setOpenItem(null);
    try {
      await api.post("/doses", { med_id: item.med_id, date, time: item.time, status, now: nowHM(), ...extra });
    } catch {
      toast.error("Could not save that action");
    }
    refresh();
  };

  const label = useMemo(() => (isToday ? "Today" : fmtDayLabel(date)), [date, isToday]);

  const go = (delta) => {
    dir.current = delta;
    setDate((d) => shiftDate(d, delta));
  };

  const header = (
    <div className="flex items-center">
      <button
        data-testid="day-prev-btn"
        onClick={() => go(-1)}
        className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors duration-200 hover:bg-slate-800 active:scale-90"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        data-testid="day-label"
        onClick={() => {
          dir.current = 0;
          setDate(todayStr());
        }}
        className={`min-w-[92px] whitespace-nowrap text-center font-semibold tracking-tight ${
          big ? "text-[19px]" : "text-[16px]"
        }`}
      >
        {label}
      </button>
      <button
        data-testid="day-next-btn"
        onClick={() => go(1)}
        className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors duration-200 hover:bg-slate-800 active:scale-90"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );

  const now = nowHM();
  const firstUpcoming = isToday ? groups.findIndex((g) => g.time > now) : -1;
  const touchX = useRef(null);

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) < 60) return;
    go(dx < 0 ? 1 : -1);
  };

  return (
    <AppFrame header={header}>
      <motion.div
        key={date}
        initial={{ opacity: 0, x: dir.current * 10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.16, ease: "easeOut" }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="day-swipe-area"
      >
      {items === null ? (
        <div className="space-y-3 pt-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-900/60" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center" data-testid="empty-schedule">
          <p className={`text-slate-400 ${big ? "text-[19px]" : "text-[15px]"}`}>Nothing scheduled for this day.</p>
          <Link
            to="/medicines"
            data-testid="empty-add-medicine-link"
            className="mt-6 inline-flex h-12 items-center gap-2 rounded-full bg-gradient-to-r from-sky-500 to-blue-600 px-6 text-[15px] font-semibold text-white active:scale-95"
          >
            <Plus className="h-4 w-4" /> Add medicine
          </Link>
        </div>
      ) : (
        <div data-testid="day-timeline">
          {groups.map((g, gi) => {
            const overdue = isToday && g.time <= now;
            return (
              <div key={g.time}>
                {gi === firstUpcoming && firstUpcoming > 0 && (
                  <div className={`flex items-center gap-2 ${big ? "my-3" : "my-1"}`} data-testid="now-divider">
                    <span className={`font-semibold text-sky-300 ${big ? "text-[14px]" : "text-[11px]"}`}>
                      now · {fmt12(now)}
                    </span>
                    <span className="h-px flex-1 bg-gradient-to-r from-sky-400/70 via-sky-400/25 to-transparent shadow-[0_0_10px_rgba(56,189,248,0.5)]" />
                  </div>
                )}

                <div
                  data-testid={`time-group-${g.time}`}
                  className={`-mx-4 border-t px-4 ${big ? "py-2.5" : "flex gap-2 py-1"} ${
                    overdue ? "border-sky-400/20 bg-sky-500/[0.06]" : "border-slate-800/50"
                  }`}
                >
                  {big ? (
                    <p
                      className={`mb-2 text-[17px] font-semibold ${overdue ? "text-sky-300" : "text-slate-400"}`}
                    >
                      {fmt12(g.time)}
                    </p>
                  ) : (
                    <div className="w-[60px] shrink-0 pt-[9px]">
                      <p
                        className={`whitespace-nowrap text-[12.5px] font-semibold ${
                          overdue ? "text-sky-300" : "text-slate-500"
                        }`}
                      >
                        {fmt12(g.time)}
                      </p>
                    </div>
                  )}

                  <div
                    className={
                      big ? "space-y-2" : "min-w-0 flex-1 divide-y divide-slate-800/25"
                    }
                  >
                    {g.list.map((item) => {
                      const key = `${item.med_id}-${item.time}`;
                      return (
                        <div
                          key={key}
                          data-testid={`dose-row-${key}`}
                          className={`flex items-center ${
                            big
                              ? "gap-3 rounded-2xl border border-slate-800 bg-slate-900/55 px-3.5 py-3"
                              : "gap-1.5 py-1"
                          }`}
                        >
                          <div
                            data-testid={`dose-open-${key}`}
                            onClick={() => setOpenItem(item)}
                            className={`min-w-0 flex-1 cursor-pointer pr-0.5 ${
                              big ? "" : "flex items-center gap-1.5"
                            }`}
                          >
                            <div className={big ? "flex items-baseline gap-2" : "contents"}>
                              <p
                                className={`font-medium leading-snug text-slate-50 ${
                                  big ? "min-w-0 break-words text-[21px]" : "truncate text-[15px]"
                                }`}
                              >
                                {item.name}
                              </p>
                              {counters[key] && (
                                <span
                                  className={`shrink-0 text-slate-500 ${big ? "text-[15px]" : "text-[11px]"}`}
                                  data-testid={`dose-counter-${key}`}
                                >
                                  {counters[key]}
                                </span>
                              )}
                            </div>
                            <Meta item={item} isToday={isToday} testKey={key} big={big} />
                          </div>

                          <Action testid={`taken-btn-${key}`} tone="green" big={big} onClick={() => act(item, "taken")}>
                            <Check className={big ? "h-6 w-6" : "h-4 w-4"} />
                          </Action>
                          <Action testid={`skip-btn-${key}`} tone="red" big={big} onClick={() => act(item, "skipped")}>
                            <X className={big ? "h-6 w-6" : "h-4 w-4"} />
                          </Action>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}

          {groups.length === 0 && (
            <p className={`py-10 text-center text-slate-400 ${big ? "text-[19px]" : "text-[15px]"}`} data-testid="all-done-msg">
              All done for this day.
            </p>
          )}
        </div>
      )}

      {done.length > 0 && (
        <div
          className="mt-6 -mx-4 border-t border-slate-800 bg-slate-950/60 px-4 pb-2 pt-1"
          data-testid="done-drawer"
        >
          <button
            data-testid="done-toggle-btn"
            onClick={() => setShowDone((s) => !s)}
            className="flex w-full items-center py-2.5"
          >
            <span className={`text-slate-500 ${big ? "text-[17px]" : "text-[15px]"}`}>Done · {done.length}</span>
            <ChevronDown
              className={`ml-auto h-5 w-5 text-slate-600 transition-transform duration-200 ${
                showDone ? "rotate-180" : ""
              }`}
            />
          </button>
          {showDone && (
            <div>
              {done.map((item) => {
                const key = `${item.med_id}-${item.time}`;
                const taken = item.status === "taken";
                return (
                  <div
                    key={key}
                    data-testid={`done-row-${key}`}
                    onClick={() => setOpenItem(item)}
                    className={`flex cursor-pointer items-center gap-2 border-t border-slate-800/40 ${
                      big ? "py-2.5" : "py-1.5"
                    }`}
                  >
                    <div className={`shrink-0 ${big ? "w-[74px]" : "w-[60px]"}`}>
                      <p
                        className={`whitespace-nowrap font-semibold text-slate-600 ${
                          big ? "text-[15px]" : "text-[12.5px]"
                        }`}
                      >
                        {fmt12(item.time)}
                      </p>
                    </div>
                    <div
                      className={`min-w-0 flex-1 ${big ? "" : "flex items-center gap-1.5"}`}
                    >
                      <div className={big ? "flex items-baseline gap-2" : "contents"}>
                        <p className={`text-slate-400 ${big ? "break-words text-[18px]" : "truncate text-[14.5px]"}`}>
                          {item.name}
                        </p>
                        {counters[key] && (
                          <span
                            className={`shrink-0 text-slate-600 ${big ? "text-[14px]" : "text-[11px]"}`}
                            data-testid={`done-counter-${key}`}
                          >
                            {counters[key]}
                          </span>
                        )}
                      </div>
                      <span
                        data-testid={`done-status-${key}`}
                        className={`flex shrink-0 items-center gap-1 ${big ? "mt-1 text-[15px]" : "text-[11px]"} ${
                          taken ? "text-emerald-400/80" : "text-red-400/80"
                        }`}
                      >
                        {taken ? (
                          <Check className={`shrink-0 ${big ? "h-4 w-4" : "h-3 w-3"}`} />
                        ) : (
                          <X className={`shrink-0 ${big ? "h-4 w-4" : "h-3 w-3"}`} />
                        )}
                        <span className="whitespace-nowrap">
                          {big && (taken ? "Taken" : "Skipped")}
                          {big && fmtActed(item) ? ` at ${fmtActed(item)}` : ""}
                          {!big && (fmtActed(item) || (taken ? "Taken" : "Skipped"))}
                        </span>
                      </span>
                    </div>
                    <Action testid={`undo-btn-${key}`} tone="slate" big={big} onClick={() => act(item, "pending")}>
                      <RotateCcw className={big ? "h-5 w-5" : "h-[15px] w-[15px]"} />
                    </Action>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      </motion.div>

      {openItem && (
        <DoseDetail
          item={openItem}
          status={effectiveStatus(openItem, date)}
          onClose={() => setOpenItem(null)}
          onAction={(status, extra) => act(openItem, status, extra)}
        />
      )}
    </AppFrame>
  );
}
